export default class CreditCard {

    static get CardRulesRegex() {
        return { // Contains the regex tests for accepted cards
            Brands: { // Contains the regex tests for accepted cards
                amex: /^3[47][0-9]{13}$/,
                discover: /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
                diners_club: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
                jcb: /^(?:2131|1800|35[0-9]{3})[0-9]{11}$/,
                mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
                visa: /^4[0-9]{12}(?:[0-9]{3})?$/
            },
            BrandMaxLength: { // Contains the length regex tests for cards
                amex: /^3[47]\d{0,13}$/,                      // 15 digits.
                diners_club: /^3(?:0[0-5]|[68]\d)\d{0,11}$/,  // 14 digits.
                standard: /^\d{0,16}$/,                       // 16 digits.
            }
        }
    }

    constructor() {
        this.number     = document.getElementById("ccnumber");
        this.expiration = document.getElementById("ccexpiration");
        this.csc        = document.getElementById("ccsecuritycode");
        this.type       = document.getElementById("card_type");
        this.postal     = document.getElementById("ccpostalcode");
        this.isValid    = false;
        // card number.
        this.number.addEventListener("input", (e) => { this.handleCardNumberInput(e) }, false);
        this.number.addEventListener("focus", (e) => { this.handleRemoveValidation(e) }, false);
        // expiration date.
        this.expiration.addEventListener("input",  (e) => { this.handleExpirationDateInput(e) }, false);
        this.expiration.addEventListener("focus",  (e) => { this.handleRemoveValidation(e) }, false);
        this.expiration.addEventListener('keydown', () => { this.handleTabOnBackspace(this.number) }, false);
        // security code.
        this.csc.addEventListener("input",  (e) => { this.handleSecurityCodeInput(e) }, false);
        this.csc.addEventListener("focus",  (e) => { this.handleRemoveValidation(e) }, false);
        this.csc.addEventListener('keydown', () => { this.handleTabOnBackspace(this.expiration) }, false);
        // postal code.
        if (this.postal) {
            this.postal.addEventListener("focus",  (e) => { this.handleRemoveValidation(e) }, false);
            this.postal.addEventListener('keydown', () => { this.handleTabOnBackspace(this.csc) }, false);
        }
    }

    /**
     * Handles the real-time formatting & validation of the card number input value.
     * @param {any} e
     */
    handleCardNumberInput(e) {
        const inputValue = e.target.value;
        const formattedCardNumber = this.formatCardNumber(inputValue);
        this.changeCardNumberCursorPosition(e, inputValue, formattedCardNumber);
        this.isValid = this.validateCardNumber(inputValue);
    }

    /**
     * Handles the real-time formatting & validation of the expiration date input value.
     * @param {any} e
     */
    handleExpirationDateInput(e) {
        const allowedKeys = [8];
        if (allowedKeys.indexOf(e.keyCode) !== -1) return;
        e.target.value = this.formatExpirationDate(e.target.value);
        this.changeExpirationDateCursorPosition(e);
    }

    /**
     * Handles the real-time validation of the security code input value.
     * @param {any} e
     */
    handleSecurityCodeInput(e) {
        this.changeSecurityCodeCursorPosition(e);
        this.validateSecurityCode(e.target.value, this.number.value);
    }

    /**
     * Handle input focus on backspace.
     * @param {any} input
     */
    handleTabOnBackspace(input) {
        const { key } = event;

        if (key === "Backspace" && event.target.selectionStart === 0) {
            input.focus();
            input.selectionStart = input.selectionEnd = input.value.length;
        }
    }

    /**
     * Handle removal of validation error styles.
     * @param {any} e
     */
    handleRemoveValidation(e) {
        e.target.classList.remove("input-validation-error");
    }

    /**
     * Apply credit card number formatting.
     * @param {any} cardNumberValue
     */
    formatCardNumber(cardNumberValue) {

        const number = this.sanitizeInput(cardNumberValue);
        let formattedCardNumber = "";
        let maxLength = 0;

        if (CreditCard.CardRulesRegex.BrandMaxLength.amex.test(number)) {
            formattedCardNumber = number.replace(/(\d{4})/, "$1 ").replace(/(\d{4}) (\d{6})/, "$1 $2 ");
            maxLength = 17;
        } else if (CreditCard.CardRulesRegex.BrandMaxLength.diners_club.test(number)) {
            formattedCardNumber = number.replace(/(\d{4})/, "$1 ").replace(/(\d{4}) (\d{6})/, "$1 $2 ");
            maxLength = 16;
        } else if (CreditCard.CardRulesRegex.BrandMaxLength.standard.test(number)) {
            formattedCardNumber = number.replace(/(\d{4})/, "$1 ").replace(/(\d{4}) (\d{4})/, "$1 $2 ").replace(/(\d{4}) (\d{4}) (\d{4})/, "$1 $2 $3 ");
            maxLength = 19;
        }

        // Set card number and security code element data.
        this.number.value = formattedCardNumber;
        this.number.setAttribute("maxlength", maxLength);
        this.number.setAttribute("data-lastvalue", formattedCardNumber);
        this.csc.setAttribute("maxlength", maxLength === 17 ? 4 : 3); // change csc max length to 4 for amex.

        return formattedCardNumber;
    }

    /**
     * Apply expiration date formatting/validation.
     * @param {any} expirationDateValue
     */
    formatExpirationDate(expirationDateValue) {
        return expirationDateValue                             // Conversion details:
            .replace(/^([1-9]\/|[2-9])$/g, "0$1/")             // 3   -> 03/
            .replace(/^(0[1-9]|1[0-2])$/g, "$1/")              // 11  -> 11/
            .replace(/^([0-1])([3-9])$/g, "0$1/$2")            // 13  -> 01/3
            .replace(/^(0?[1-9]|1[0-2])([0-9]{2})$/g, "$1/$2") // 141 -> 01/41
            .replace(/^([0]+)\/|[0]+$/g, "0")                  // 0/  -> 0 and 00 > 0
            .replace(/[^\d\/]|^[\/]*$/g, "")                   // To allow only digits and forward slash
            .replace(/\/\//g, "/");                            // Prevent entering more than 1 forward slash
    }

    /**
     * Update cursor position to match formatted card number value.
     * @param {any} event
     * @param {any} inputValue
     * @param {any} formattedCardNumber
     */
    changeCardNumberCursorPosition(event, inputValue, formattedCardNumber) {
        let cursor = event.target.selectionStart;
        if (cursor === inputValue.length) {
            cursor = formattedCardNumber.length;
            if (this.number.getAttribute("data-lastvalue") && this.number.getAttribute("data-lastvalue").charAt(cursor - 1) == " ") {
                cursor--;
            }
        }
        if (inputValue != formattedCardNumber) {
            if (inputValue.charAt(cursor) == " " && formattedCardNumber.charAt(cursor - 1) == " ") {
                cursor++;
            }
        }
        // Move cursor to next input if necessary.
        event.target.selectionStart = event.target.selectionEnd = cursor;
        if (document.activeElement === this.number && cursor.toString() === this.number.getAttribute("maxlength")) {
            this.expiration.focus();
        }          
    }

    /**
     * Update cursor position to match formatted expiration date value.
     * @param {any} event
     */
    changeExpirationDateCursorPosition(event) {
        const cursor = event.target.selectionStart;
        if (cursor === 3 && event.inputType === "deleteContentBackward") {
            event.target.value = event.target.value[0];
        }
        // Move cursor to next input if necessary.
        event.target.selectionStart = event.target.selectionEnd = cursor;
        if (document.activeElement === this.expiration && cursor.toString() === this.expiration.getAttribute("maxlength")) {
            this.csc.focus();
        }
    }

    /**
     * Update cursor position to the next input if necessary.
     * @param {any} event
     */
    changeSecurityCodeCursorPosition(event) {
        const cursor = event.target.selectionStart;
        event.target.selectionStart = event.target.selectionEnd = cursor;
        
        if (document.activeElement === this.csc && cursor.toString() === this.csc.getAttribute("maxlength") && this.postal) {
            this.postal.focus();
        }
    }

    /**
     * Validate card number passes Luhn check.
     * @param {any} cardNumber
     */
    validateCardNumber(cardNumber) {
        const lastValue = this.sanitizeInput(cardNumber);

        let sum = 0;
        let shouldDouble = false;

        for (let i = lastValue.length - 1; i >= 0; i--) {
            let digit = parseInt(lastValue.charAt(i));
            if (shouldDouble) {
                if ((digit *= 2) > 9) digit -= 9;
            }
            sum += digit;
            shouldDouble = !shouldDouble;
        }
        return ((sum % 10) === 0);
    }

    /**
     * Validate security code length.
     * @param {any} securityCode
     * @param {any} cardNumber
     */
    validateSecurityCode(securityCode, cardNumber) {
        securityCode = this.sanitizeInput(securityCode);
        cardNumber   = this.sanitizeInput(cardNumber);

        if ((CreditCard.CardRulesRegex.Brands.amex).test(cardNumber)) {
            return ((/^\d{4}$/).test(securityCode)); // amex: 4 digits.
        } else {
            return ((/^\d{3}$/).test(securityCode)); // default: 3 digits.
        }
    }

    sanitizeInput(e) {
        return e.replace(/\D/g, "");
    }

    getExpirationDateTime(value) {
        var MM = parseInt(value.substring(0, 2));
        var YYYY = parseInt(20 + value.substring(3));
        const date = new Date(Date.UTC(YYYY, MM));
        return date;
    }

    getCardData() {
        return {
            CardNumber: this.sanitizeInput(this.number.value),
            ExpirationDate: this.getExpirationDateTime(this.expiration.value),
            SecurityCode: this.sanitizeInput(this.csc.value),
            //ContactName: payer.CardholderName,
            //Address: payer.BillingAddress
        };
    }

}